import { useMemo, type CSSProperties } from 'react';
import { qPatientFiles, type PatientFileRow } from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';
import { NavIcon } from './shared';
import { formatDateShort, getFileExtension } from '../pages/patient/consultation/consultation-data';

// PRD.286 — Patient sidebar Files & Images panel.
//
// Read-only summary of the patient's master file repository. Renders the same
// surface in both the expanded sidebar body and the collapsed-rail right
// drawer. The "View All" CTA navigates to the full /patient/:id/files page,
// which is where uploads (with Record Date + consultation matching), preview,
// and edits happen.

interface SidebarFilesPanelProps {
  patientId: string;
  /** Called when the user clicks "View All". */
  onViewAll: () => void;
}

const cardStyle: CSSProperties = {
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  padding: 'var(--sp-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};

const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const sectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text)',
};

const viewAllBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

export function SidebarFilesPanel({ patientId, onViewAll }: SidebarFilesPanelProps) {
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(qPatientFiles(patientId));

  const counts = useMemo(() => {
    let images = 0;
    let documents = 0;
    let unassigned = 0;
    for (const r of fileRows) {
      if (r.kind === 'image') images += 1;
      else documents += 1;
      if (r.encounter_id == null) unassigned += 1;
    }
    return { total: fileRows.length, images, documents, unassigned };
  }, [fileRows]);

  // Rows already arrive ordered by created_at DESC (qPatientFiles).
  const recent = fileRows.slice(0, 6);

  return (
    <>
      <div style={cardStyle}>
        <div style={sectionHeaderStyle}>
          <span style={sectionTitleStyle}>
            <NavIcon name="folder-open" /> Files
          </span>
          <button onClick={onViewAll} style={viewAllBtnStyle}>
            View All <NavIcon name="chevron-right" />
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          The patient&rsquo;s master repository for every uploaded file.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 'var(--sp-2)',
          }}
        >
          <SummaryChip label="Images" value={counts.images} tone="info" />
          <SummaryChip label="Documents" value={counts.documents} tone="neutral" />
          <SummaryChip label="Unassigned" value={counts.unassigned} tone="warning" />
        </div>
      </div>

      {recent.length > 0 && (
        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <span style={sectionTitleStyle}>
              <NavIcon name="folder-open" /> Recent
            </span>
            <button onClick={onViewAll} style={viewAllBtnStyle}>
              View All <NavIcon name="chevron-right" />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {recent.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={onViewAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-2)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface-2)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-sm)',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {r.kind === 'image' && r.url ? (
                    <img
                      src={r.url}
                      alt={r.filename ?? ''}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <NavIcon name={r.kind === 'image' ? 'image' : 'file-text'} />
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={r.filename ?? ''}
                  >
                    {r.filename ?? 'Untitled'}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {getFileExtension(r.filename ?? '') || 'FILE'}
                    {r.encounter_id == null ? ' · Unassigned' : ''}
                    {r.record_date ? ` · ${formatDateShort(r.record_date)}` : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'info' | 'warning' | 'success' | 'neutral';
}) {
  const palette = {
    info: { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' },
    warning: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
    success: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
    neutral: { bg: '#e0e7ff', color: '#3730a3' },
  }[tone];
  return (
    <div
      style={{
        padding: 'var(--sp-2)',
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: palette.color }}>
        {value}
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: palette.color }}>{label}</span>
    </div>
  );
}
